/**
 * Kostprijs ↔ opbrengst vergelijk-weergave (#71, batch-9 stream kostprijs).
 *
 * Brengt de medewerker-kostprijs en de klant-opbrengst samen in één
 * **klant- en rooster-gekoppelde** weergave, **instelbaar per uur/week/maand**,
 * met marge (€ en %) die tegen instelbare grenzen (`Client.minimumMargePct` +
 * een doelmarge) wordt getoetst.
 *
 * Bouwt voort op de bestaande bouwstenen — NIETS opnieuw:
 *  - `computeEmployeeCost` (@streep/utils) levert de jaar-kostcomponenten;
 *  - `computeCalculationLine`/`sumCalculationTotals`/`evaluateCalculationWarnings`/
 *    `computeAdviceRate` (de calculatie-engine) doen het rekenwerk + de marge +
 *    de advies-tarief/waarschuwingen.
 *
 * De pure rekenlogica leeft in `@streep/utils/kostprijs-vergelijk`; dit bestand
 * levert de gedeelde types + het request-schema voor `POST /calculations/vergelijk`.
 */

import { z } from 'zod'
import type { CalculationWarning } from './calculation'

// ─── Declarabele uren (Deel 3 — de deler) ────────────────────────────────────

/**
 * Invoer voor `computeDeclarabeleUren`: de werkbare (declarabele) uren per jaar
 * uit de contracturen min ATV, verlof en ziekte. Eén mechanisme voor ziekte
 * (urenaftrek) — de aparte ziekteverzuim-kostenopslag staat in de vergelijk-
 * weergave op 0 zodat ziekte niet dubbel telt (Jelle's eigen punt).
 */
export interface DeclarabeleUrenInput {
  /** Bruto werkbare uren/jaar vóór aftrek (= werkbare uren/week × weken/jaar). */
  brutoUrenJaar: number
  /** ATV-uren/jaar die niet declarabel zijn (0 wanneer geen ATV-regeling). */
  atvUrenJaar: number
  /** Verlof-uren/jaar (uit `Employee.leaveBalance`/`LeaveEntitlement` of parameter). */
  verlofUrenJaar: number
  /** Kortdurend ziekteverzuim als % van de bruto uren (langdurig = verzekerd). */
  ziektePct: number
}

export interface DeclarabeleUrenResult {
  brutoUrenJaar: number
  atvUrenJaar: number
  verlofUrenJaar: number
  ziekteUrenJaar: number
  /** Werkbare/declarabele uren/jaar = bruto - ATV - verlof - ziekte (deler). */
  declarabeleUren: number
}

// ─── Situatie-vergelijk (Deel 1/2/6/9) ───────────────────────────────────────

/** Eén bedrag in vier weergaven. De jaarbasis is leidend; uur/week/maand zijn
 *  afgeleid (uur = jaar/declarabele uren, week = jaar/weken, maand = jaar/12). */
export interface PeriodeBedrag {
  perUur: number
  perWeek: number
  perMaand: number
  perJaar: number
}

/**
 * Invoer voor `computeSituatieVergelijk`. De jaar-kostcomponenten komen uit
 * `computeEmployeeCost` (de service voedt ze aan, met ziekte als urenaftrek i.p.v.
 * kostenopslag); de situatie-componenten (brandstof via live klant-afstand,
 * reisuren, ploegentoeslag) en de opbrengst-tarieven komen uit klant + rooster.
 */
export interface SituatieVergelijkInput {
  // Jaar-kostcomponenten van de medewerker (integrale kostprijs-opbouw).
  loonkostenJaar: number
  /** Auto-kosten excl. de situatie-brandstof (lease/verzekering/onderhoud/afschrijving). */
  autokostenJaar: number
  bijkomendeKostenJaar: number
  /** Brandstof/jaar o.b.v. de LIVE klant-afstand (vervangt de generieke woon-werk). */
  brandstofJaar: number

  // Deler + periode-delers.
  declarabeleUren: number
  wekenPerJaar: number

  // Reisuren-situatie (kostkant verloond tegen bruto uurloon).
  reisurenJaar: number
  brutoUurloon: number
  kmJaar: number

  // Opbrengst-tarieven (klant).
  basistariefPerUur: number
  /** Reistijd-opbrengst/uur — al begrensd (cap) door de service. */
  reisUurtariefOpbrengst: number
  /** Km-vergoeding opbrengst (€/km, bv. 0,30). */
  kmTariefOpbrengst: number

  // Ploegentoeslag voor het gekozen rooster, al naar % omgerekend.
  /** VERLONING — verhoogt de loonkost. */
  ploegToeslagKostPct: number
  /** FACTURATIE — verhoogt de loon-omzet. */
  ploegToeslagOpbrengstPct: number
  /** True bij een ploegendienst-rooster (2-/3-/4-/5-ploegen) — voor de
   *  "ploegentoeslag verwacht maar 0"-waarschuwing. */
  dienstTypeHeeftPloeg?: boolean

  // Grenzen.
  minimumMargePct: number | null
  gewensteMargePct: number | null
}

/** Jaar-breakdown per component (voor de UI-uitleg naast de totalen). */
export interface SituatieVergelijkComponenten {
  kostLoonJaar: number
  /** Auto + bijkomende + brandstof. */
  kostNietLoonJaar: number
  kostReisJaar: number
  opbrengstArbeidJaar: number
  opbrengstReisJaar: number
  opbrengstKmJaar: number
}

export interface SituatieVergelijkResult {
  kostprijs: PeriodeBedrag
  opbrengst: PeriodeBedrag
  marge: PeriodeBedrag
  /** Marge% t.o.v. de omzet (engine-conventie: marge / opbrengst × 100). */
  margePct: number
  /** Advies-verkooptarief/uur = kostprijs/uur / (1 - doelmarge%); null zonder doel. */
  adviesTariefPerUur: number | null
  declarabeleUren: number
  wekenPerJaar: number
  componenten: SituatieVergelijkComponenten
  warnings: CalculationWarning[]
}

// ─── Request-schema voor POST /calculations/vergelijk ────────────────────────

export const situatieVergelijkRequestSchema = z.object({
  employeeId: z.string().min(1),
  clientId: z.string().min(1),
  /** Rooster: ploegsoort + ploegensysteem bepalen de geldende ploegentoeslag. */
  ploegSoort: z.enum(['DAG', 'VROEG', 'LAAT', 'NACHT']).nullable().optional(),
  dienstType: z
    .enum(['DAGDIENST', 'PLOEGEN_2', 'PLOEGEN_3', 'PLOEGEN_4', 'PLOEGEN_5', 'CONSIGNATIE'])
    .nullable()
    .optional(),
  dagType: z.enum(['WERKDAG', 'ZATERDAG', 'ZONDAG', 'FEESTDAG']).optional(),
  // Handmatige overrides (Deel 5/6) — situatie doorrekenen zonder maps.
  afstandKmOverride: z.number().min(0).max(10000).nullable().optional(),
  reisurenPerDagOverride: z.number().min(0).max(24).nullable().optional(),
  /** Reistijd-opbrengst/uur handmatig + begrensbaar (cap). */
  reisUurtariefOpbrengstOverride: z.number().min(0).max(10000).nullable().optional(),
  /** Doelmarge% voor het advies-tarief (los van de klant-minimummarge). */
  gewensteMargePct: z.number().min(0).max(99.999).nullable().optional(),
})

export type SituatieVergelijkRequest = z.infer<typeof situatieVergelijkRequestSchema>

/**
 * batch-62 kostprijs-werkelijk (FO-460/461) — de WERKELIJKE kostprijs van de
 * medewerker (laatste 12 volledige maanden: loonstrook + brandstoffacturen +
 * voertuig, gedeeld door de productieve uren) naast de norm uit het kostprofiel.
 * Informatief naast de situatieberekening (die modelleert een klant/rooster).
 */
export interface SituatieVergelijkWerkelijk {
  /** WERKELIJK zodra er genoeg volledige maanden zijn; anders NORM. */
  kostprijsBron: 'WERKELIJK' | 'NORM'
  maandenMetData: number
  kostprijsPerProductiefUur: number | null
  normKostprijsPerUur: number | null
  /** Gemiddelde werkelijke brandstofkosten per maand (excl. btw, excl. privé). */
  brandstofMaandGemiddeld: number | null
}

/** API-respons: het berekende resultaat + de gekozen context (provenance). */
export interface SituatieVergelijkResponse extends SituatieVergelijkResult {
  employeeId: string
  clientId: string
  employeeNaam: string | null
  clientNaam: string | null
  /** Bron van de gebruikte afstand (live route, cache, handmatig of geen). */
  afstandKm: number | null
  afstandBron: 'LIVE' | 'HANDMATIG' | 'GEEN'
  ploegSoort: string | null
  dienstType: string | null
  /** batch-62 — werkelijke kostprijs naast de norm (optioneel voor oudere clients). */
  werkelijk?: SituatieVergelijkWerkelijk
}
